/**
 * Camadas de imagem do miolo.
 *
 * Cada camada é uma "figura" posicionada em milímetros na página: fundo de
 * página inteira, faixa de borda, canto ou posição livre. Todas podem ser
 * dimensionadas, giradas, esmaecidas e distribuídas entre as páginas.
 */
import { limitar } from '../color/convert.js';
import { PAPEIS, medirPaginaDoMiolo } from './papel.js';
/** Limites generosos: negativo sangra fora da página, positivo empurra para dentro. */
export const LIMITE_POSICAO_MM = { min: -200, max: 600 };
/** Incrementos usados pelos botões de posicionamento. */
export const PASSOS_POSICAO_MM = [0.5, 1, 5];
/** Tamanho padrão de uma camada de canto, em fração do lado menor da página. */
export const FRACAO_CANTO_PADRAO = 0.28;
export function proporcaoEfetiva(camada) {
    return camada.proporcao && camada.proporcao > 0 ? camada.proporcao : 1;
}
/** Altura final: proporcional à imagem ou o valor definido manualmente. */
export function alturaEfetiva(camada) {
    const largura = Math.max(1, camada.larguraMm);
    return camada.proporcional ? largura / proporcaoEfetiva(camada) : Math.max(1, camada.alturaMm);
}
function ancoradaNaDireita(camada) {
    if (camada.papel === 'CANTO')
        return camada.canto === 'SUP_DIR' || camada.canto === 'INF_DIR';
    if (camada.papel === 'BORDA')
        return camada.lado === 'DIREITA';
    return false;
}
function ancoradaNaBase(camada) {
    if (camada.papel === 'CANTO')
        return camada.canto === 'INF_DIR' || camada.canto === 'INF_ESQ';
    if (camada.papel === 'BORDA')
        return camada.lado === 'BASE';
    return false;
}
/**
 * Posiciona a camada na página.
 *
 * `xMm`/`yMm` são distâncias da âncora do papel escolhido (borda ou canto), e
 * não coordenadas absolutas — assim as setas de posicionamento movem a imagem
 * para o lado certo mesmo quando ela está ancorada à direita ou à base.
 */
export function resolverCamada(camada, medidas, espelhada, temaEscuro) {
    const larguraMm = Math.max(1, camada.larguraMm);
    const alturaMm = alturaEfetiva(camada);
    let esquerdaMm = camada.xMm;
    let topoMm = camada.yMm;
    if (camada.papel === 'BORDA') {
        if (camada.lado === 'DIREITA')
            esquerdaMm = medidas.larguraMm - camada.xMm - larguraMm;
        if (camada.lado === 'BASE')
            topoMm = medidas.alturaMm - camada.yMm - alturaMm;
    }
    if (camada.papel === 'CANTO') {
        if (camada.canto === 'SUP_DIR' || camada.canto === 'INF_DIR') {
            esquerdaMm = medidas.larguraMm - camada.xMm - larguraMm;
        }
        if (camada.canto === 'INF_DIR' || camada.canto === 'INF_ESQ') {
            topoMm = medidas.alturaMm - camada.yMm - alturaMm;
        }
    }
    if (espelhada) {
        esquerdaMm = medidas.larguraMm - esquerdaMm - larguraMm;
    }
    return {
        camada,
        esquerdaMm,
        topoMm,
        larguraMm,
        alturaMm,
        rotacaoGraus: camada.rotacaoGraus,
        temaEscuro,
    };
}
/**
 * Move a camada em milímetros, no sentido visual (direita/baixo positivos),
 * já convertendo para a âncora correta.
 */
export function moverCamada(camada, deslocamentoXMm, deslocamentoYMm) {
    const sinalX = ancoradaNaDireita(camada) ? -1 : 1;
    const sinalY = ancoradaNaBase(camada) ? -1 : 1;
    return {
        ...camada,
        xMm: arredondar(limitar(camada.xMm + deslocamentoXMm * sinalX, LIMITE_POSICAO_MM.min, LIMITE_POSICAO_MM.max)),
        yMm: arredondar(limitar(camada.yMm + deslocamentoYMm * sinalY, LIMITE_POSICAO_MM.min, LIMITE_POSICAO_MM.max)),
    };
}
export function redimensionarCamada(camada, deltaLarguraMm, deltaAlturaMm) {
    const larguraMm = limitar(arredondar(camada.larguraMm + deltaLarguraMm), 4, 1200);
    const proxima = { ...camada, larguraMm };
    if (camada.proporcional) {
        return { ...proxima, alturaMm: arredondar(larguraMm / proporcaoEfetiva(camada)) };
    }
    return { ...proxima, alturaMm: limitar(arredondar(camada.alturaMm + deltaAlturaMm), 4, 1600) };
}
/** Centraliza a camada no eixo pedido, respeitando as margens de encadernação. */
export function centralizarCamada(camada, medidas, eixo) {
    const alturaMm = alturaEfetiva(camada);
    const larguraMm = Math.max(1, camada.larguraMm);
    let xMm = camada.xMm;
    let yMm = camada.yMm;
    if (eixo === 'HORIZONTAL' || eixo === 'AMBOS') {
        xMm = arredondar((medidas.larguraMm - larguraMm) / 2);
    }
    if (eixo === 'VERTICAL' || eixo === 'AMBOS') {
        yMm = arredondar((medidas.alturaMm - alturaMm) / 2);
    }
    return { ...camada, papel: 'LIVRE', xMm, yMm };
}
/** Faz a camada preencher a página inteira (área total, incluindo sangria). */
export function preencherPagina(camada, medidas) {
    return {
        ...camada,
        papel: 'PAGINA_INTEIRA',
        xMm: 0,
        yMm: 0,
        larguraMm: medidas.larguraMm,
        alturaMm: medidas.alturaMm,
        proporcional: false,
    };
}
/** Encaixa a camada nos limites de uma borda, mantendo a proporção da imagem. */
export function encaixarNaBorda(camada, medidas, lado) {
    const proporcao = proporcaoEfetiva(camada);
    const horizontal = lado === 'TOPO' || lado === 'BASE';
    const larguraMm = horizontal ? medidas.larguraMm : Math.min(medidas.larguraMm, medidas.alturaMm * 0.4);
    const alturaMm = larguraMm / proporcao;
    return {
        ...camada,
        papel: 'BORDA',
        lado,
        xMm: 0,
        yMm: 0,
        larguraMm: arredondar(larguraMm),
        alturaMm: arredondar(alturaMm),
        proporcional: true,
    };
}
function arredondar(valor, casas = 1) {
    const fator = 10 ** casas;
    return Math.round(valor * fator) / fator;
}
/* ------------------------------------------------------------------ */
/* Distribuição entre páginas                                          */
/* ------------------------------------------------------------------ */
/** Lê "1,3,5-10" em um conjunto de páginas (1-based). */
export function lerPaginas(expressao) {
    const paginas = new Set();
    for (const parte of (expressao ?? '').split(',')) {
        const trecho = parte.trim();
        if (!trecho)
            continue;
        const intervalo = /^(\d+)\s*-\s*(\d+)$/.exec(trecho);
        if (intervalo) {
            const inicio = Number.parseInt(intervalo[1], 10);
            const fim = Number.parseInt(intervalo[2], 10);
            for (let pagina = Math.min(inicio, fim); pagina <= Math.max(inicio, fim); pagina += 1) {
                if (pagina > 0)
                    paginas.add(pagina);
            }
            continue;
        }
        const numero = Number.parseInt(trecho, 10);
        if (Number.isFinite(numero) && numero > 0)
            paginas.add(numero);
    }
    return paginas;
}
function escopoAtende(escopo, tipo) {
    switch (escopo) {
        case 'CAPA':
            return tipo === 'CAPA';
        case 'CONTRACAPA':
            return tipo === 'CONTRACAPA';
        case 'DIVISORIAS':
            return tipo === 'MES' || tipo === 'DIVISORIA';
        case 'DIAS':
            return tipo === 'DIA';
        case 'CADERNOS':
            return tipo === 'CADERNO';
        case 'CONTEUDO':
            // Páginas de planejamento. Cadernos ficam de fora de propósito: o
            // caderno de desenho, por exemplo, não deve receber fundo por engano.
            return (tipo === 'DIA' ||
                tipo === 'ANO' ||
                tipo === 'METAS' ||
                tipo === 'ANIVERSARIOS' ||
                tipo === 'INDICE' ||
                tipo === 'DADOS_PESSOAIS' ||
                tipo === 'NOTAS' ||
                tipo === 'CONTATOS');
        case 'TODAS':
        default:
            return true;
    }
}
/**
 * Converte um fundo pronto em camada de página inteira.
 *
 * A pessoa envia o modelo dela e o motor trata como camada: assim vale tudo o
 * que já existe — opacidade, véu, ajuste, espelhamento na lombada — e a data, o
 * título e o conteúdo do dia continuam sendo impressos por cima.
 */
export function camadaDoFundo(fundo, config, ordem, formato) {
    const medidas = medirPaginaDoMiolo(config);
    return {
        id: `fundo-${fundo.id}`,
        url: fundo.url,
        nome: fundo.nome,
        papel: 'PAGINA_INTEIRA',
        lado: 'TOPO',
        canto: 'SUP_ESQ',
        xMm: 0,
        yMm: 0,
        larguraMm: medidas.larguraMm,
        alturaMm: medidas.alturaMm,
        proporcional: false,
        proporcao: null,
        rotacaoGraus: 0,
        opacidade: limitar(fundo.opacidade, 0, 1),
        veu: limitar(fundo.veu, 0, 0.9),
        desfoque: 0,
        ajuste: fundo.ajuste,
        escopo: fundo.destino,
        rodizio: 'TODAS',
        aCadaNPaginas: 1,
        paginas: '',
        espelharPares: fundo.espelharPares,
        ordem,
        visivel: fundo.visivel,
    };
}
/** Todos os fundos prontos da configuração, prontos para distribuir nas páginas. */
export function camadasDosFundos(config) {
    const fundos = (config.fundos ?? []).filter((fundo) => fundo.visivel && fundo.url);
    if (fundos.length === 0)
        return [];
    // Fundos entram atrás das camadas decorativas: começam na ordem negativa.
    const menorOrdem = Math.min(0, ...(config.camadasImagem ?? []).map((camada) => camada.ordem));
    return fundos.map((fundo, indice) => camadaDoFundo(fundo, config, menorOrdem - fundos.length + indice, config.formato));
}
/** Grupos de rodízio: camadas do mesmo papel/escopo/rodízio giram juntas. */
function chaveDoGrupo(camada) {
    return `${camada.papel}|${camada.escopo}|${camada.rodizio}`;
}
/**
 * Decide quais camadas aparecem em uma página e as devolve na ordem de
 * empilhamento. O contador de grupo avança apenas nas páginas em que o escopo
 * se aplica — é o que faz um rodízio de capas funcionar com só uma capa.
 */
export function camadasDaPagina(camadas, opcoes) {
    const aplicaveis = camadas
        .filter((camada) => camada.visivel && escopoAtende(camada.escopo, opcoes.tipoPagina))
        .sort((a, b) => a.ordem - b.ordem || a.id.localeCompare(b.id));
    const grupos = new Map();
    for (const camada of aplicaveis) {
        const chave = chaveDoGrupo(camada);
        const lista = grupos.get(chave) ?? [];
        lista.push(camada);
        grupos.set(chave, lista);
    }
    const selecionadas = [];
    const avancados = new Set();
    for (const camada of aplicaveis) {
        const chave = chaveDoGrupo(camada);
        const grupo = grupos.get(chave);
        if (camada.rodizio === 'TODAS') {
            selecionadas.push(camada);
            continue;
        }
        if (camada.rodizio === 'PAGINAS') {
            if (lerPaginas(camada.paginas).has(opcoes.indicePagina + 1))
                selecionadas.push(camada);
            continue;
        }
        // Para ALTERNADA e A_CADA_N o índice do grupo é o quanto já avançamos.
        if (!avancados.has(chave)) {
            opcoes.contador.set(chave, (opcoes.contador.get(chave) ?? 0) + 1);
            avancados.add(chave);
        }
        const passo = opcoes.contador.get(chave) ?? 1;
        const posicaoNoGrupo = grupo.findIndex((item) => item.id === camada.id);
        if (camada.rodizio === 'ALTERNADA') {
            if ((passo - 1) % grupo.length === posicaoNoGrupo)
                selecionadas.push(camada);
            continue;
        }
        // A_CADA_N: cada camada ocupa N páginas seguidas antes de passar a vez.
        const fatia = Math.max(1, camada.aCadaNPaginas);
        const bloco = Math.floor((passo - 1) / fatia) % grupo.length;
        if (bloco === posicaoNoGrupo)
            selecionadas.push(camada);
    }
    return selecionadas;
}
/** Cria uma camada com padrões coerentes com o formato do miolo. */
export function criarCamadaPadrao(entrada, formato, ordem = 0) {
    const papel = PAPEIS[formato];
    return {
        id: `camada-${Math.random().toString(36).slice(2, 10)}`,
        url: entrada.url,
        nome: entrada.nome,
        papel: 'PAGINA_INTEIRA',
        lado: 'TOPO',
        canto: 'SUP_ESQ',
        xMm: 0,
        yMm: 0,
        larguraMm: papel.larguraMm,
        alturaMm: papel.alturaMm,
        proporcional: false,
        proporcao: entrada.proporcao ?? null,
        rotacaoGraus: 0,
        opacidade: 0.32,
        veu: 0.55,
        desfoque: 0,
        ajuste: 'COBRIR',
        escopo: 'CAPA',
        rodizio: 'TODAS',
        aCadaNPaginas: 7,
        paginas: '',
        espelharPares: false,
        ordem,
        visivel: true,
    };
}
/** Camada decorativa de canto, com tamanho proporcional ao lado menor do papel. */
export function criarCamadaDeCanto(entrada, formato, canto, ordem = 0) {
    const papel = PAPEIS[formato];
    const ladoMenor = Math.min(papel.larguraMm, papel.alturaMm);
    const larguraMm = Math.round(ladoMenor * FRACAO_CANTO_PADRAO * 10) / 10;
    const proporcao = entrada.proporcao && entrada.proporcao > 0 ? entrada.proporcao : 1;
    return {
        ...criarCamadaPadrao(entrada, formato, ordem),
        papel: 'CANTO',
        canto,
        xMm: 6,
        yMm: 6,
        larguraMm,
        alturaMm: Math.round((larguraMm / proporcao) * 10) / 10,
        proporcional: true,
        opacidade: 1,
        veu: 0,
        escopo: 'TODAS',
    };
}
/** Reposiciona uma camada existente em outro canto, mantendo todo o resto. */
export function copiarParaCanto(camada, canto, ordem) {
    return {
        ...camada,
        id: `camada-${Math.random().toString(36).slice(2, 10)}`,
        papel: 'CANTO',
        canto,
        ordem,
    };
}
/** Reposiciona uma camada existente em outra borda. */
export function copiarParaBorda(camada, lado, medidas, ordem) {
    const base = encaixarNaBorda(camada, medidas, lado);
    return { ...base, id: `camada-${Math.random().toString(36).slice(2, 10)}`, ordem };
}
/** Converte o formato antigo (uma única imagem) em uma camada. */
export function camadaDeImagemLegada(legado, formato, ordem = 0) {
    const papel = PAPEIS[formato];
    return {
        ...criarCamadaPadrao({ url: legado.url, nome: legado.nome }, formato, ordem),
        larguraMm: papel.larguraMm,
        alturaMm: papel.alturaMm,
        opacidade: legado.opacidade,
        veu: legado.veu,
        desfoque: legado.desfoque,
        ajuste: legado.ajuste,
        escopo: legado.escopo,
    };
}
/** Índice de empilhamento livre: sempre acima das camadas anteriores. */
export function proximaOrdem(camadas) {
    return camadas.reduce((maior, camada) => Math.max(maior, camada.ordem), -1) + 1;
}
//# sourceMappingURL=imagem.js.map